import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { Loader, Plus, Check, AlertCircle } from 'lucide-react';
import StitchAdmin from './StitchAdmin';

const generateSlug = (title) =>
  title.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function CuratorAdmin({ onClose, onAddPattern, editingPatternId, initialSpecs, initialYarnColors, initialStep, initialPreview, onResetEdit, stitches, onStitchSaved, userMode, currentUser, pendingSubmissions: pendingProp, onPendingUpdate }) {
  const [step, setStep] = useState(initialStep || "url");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(initialPreview || null);
  const [error, setError] = useState("");
  const [showStitchAdmin, setShowStitchAdmin] = useState(false);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [showPendingReview, setShowPendingReview] = useState(false);

  // Load pending submissions directly when in admin mode
  useEffect(() => {
    if (userMode) return;
    const loadPending = async () => {
      const { data } = await supabase
        .from('patterns')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (data && data.length > 0) {
        setPendingSubmissions(data);
        setShowPendingReview(true);
      }
    };
    loadPending();
  }, [userMode]);

  const [pendingEditId, setPendingEditId] = useState(null);

  const loadPendingForEdit = (p) => {
    setPreview({
      title: p.title,
      description: p.description,
      image: p.image,
      sourceUrl: p.source_url,
      siteName: p.site_name || p.creator,
    });
    setSpecs({
      difficulty: p.difficulty || 'Beginner',
      category: p.category || [],
      yarnWeight: p.yarn_weight || '',
      yarnMaterial: p.yarn_material || '',
      hook: p.hook || [],
      stitches: p.stitches || [],
      specialStitches: p.special_stitches || '',
      constructionMethod: p.construction_method || '',
      gaugeEntries: p.gauge_entries || [],
      yarnYards: p.total_yards?.toString() || '',
      originalWidth: p.original_width?.toString() || '',
      originalHeight: p.original_height?.toString() || '',
      videoUrl: p.video_url || '',
      videoCreator: p.video_creator || '',
      curatorNotes: p.curator_notes || '',
      customAttribution: p.site_name || '',
      customImage: p.image || '',
      customTitle: p.title || '',
      slug: p.slug || '',
      patternContent: p.pattern_content || { sections: [{ title: "Instructions", steps: [""] }] },
      patternPhotos: p.pattern_photos || [],
    });
    setYarnSlots([]);
    const yarnColors = Array.isArray(p.yarn_colors) ? p.yarn_colors : [];
    if (yarnColors.length > 0) {
      const ids = yarnColors.map(y => y.id).filter(Boolean);
      supabase
        .from('yarn_library')
        .select('*')
        .in('id', ids)
        .then(({ data }) => {
          if (!data) return;
          const merged = yarnColors
            .map(yc => {
              const lib = data.find(d => d.id === yc.id);
              const totalYards = yc.total_yards?.toString() || (yc.skeins && lib.yards_per_skein ? (yc.skeins * lib.yards_per_skein).toString() : '');
              return lib ? { ...lib, total_yards: totalYards, role: yc.role || '' } : null;
            })
            .filter(Boolean);
          if (merged.length > 0) setYarnSlots(merged);
        });
    }
    setYarnYardsManuallySet(true); // treat loaded value as manually set so auto-calc doesn't override
    setPendingEditId(p.id);
    setStep('preview');
  };
  const [showShareDisclaimer, setShowShareDisclaimer] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const [specs, setSpecs] = useState(initialSpecs || {
    difficulty: "Beginner",
    category: [], 
    yarnWeight: "",
    yarnMaterial: "",
    hook: [], 
    stitches: initialSpecs?.stitches || [],
    specialStitches: initialSpecs?.specialStitches || "",
    constructionMethod: "",
    gaugeEntries: [],
    yarnYards: "",
    originalWidth: "",
    originalHeight: "",
    videoUrl: "",
    videoCreator: "",
    curatorNotes: "",
    customAttribution: "",
    customImage: "",
    customTitle: "",
    slug: "",
    patternContent: { sections: [{ title: "Instructions", steps: [""] }] },
    patternPhotos: [],
  });

  // Update specs when initialSpecs changes (for editing mode)
  useEffect(() => {
  if (initialSpecs) {
    setSpecs(initialSpecs);
  }
}, [initialSpecs]);

  // Yarn library state
  const [yarnSlots, setYarnSlots] = useState([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryResults, setLibraryResults] = useState([]);
  const [librarySearchLoading, setLibrarySearchLoading] = useState(false);
  const [publishPalette, setPublishPalette] = useState(false);
  const [newYarn, setNewYarn] = useState({ brand_name: '', colorway_name: '', brand_url: '', image_url: '', yards_per_skein: '', weight: '', fiber: '', total_yards: '', role: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [knownBrands, setKnownBrands] = useState([]);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  // Gauge entry form state
  const [gaugeForm, setGaugeForm] = useState({ type: 'standard', stitches: '', rows: '', unit: '4in', square_inches: '', blocks: '', note: '', label: '' });
  const [showGaugeForm, setShowGaugeForm] = useState(false);

  // Load existing yarn slots when editing a pattern (fetch yarn_library entries by IDs in yarn_colors)
  useEffect(() => {
    const yarnColors = initialYarnColors;
    if (!yarnColors || yarnColors.length === 0) return;
    const ids = yarnColors.map(y => y.id).filter(Boolean);
    if (ids.length === 0) return;
    supabase
      .from('yarn_library')
      .select('*')
      .in('id', ids)
      .then(({ data }) => {
        if (!data) return;
        // Merge library data with skeins/role from yarn_colors
        const merged = yarnColors
          .map(yc => {
            const lib = data.find(d => d.id === yc.id);
            const totalYards = (yc.skeins && lib.yards_per_skein) ? (yc.skeins * lib.yards_per_skein).toString() : '';
            return lib ? { ...lib, total_yards: totalYards, role: yc.role || '' } : null;
          })
          .filter(Boolean);
        if (merged.length > 0) setYarnSlots(merged);
      });
  }, [initialYarnColors]);

  // Auto-calculate total yarn from slots (only if user hasn't manually set it)
  const [yarnYardsManuallySet, setYarnYardsManuallySet] = useState(false);
  useEffect(() => {
    if (yarnYardsManuallySet) return;
    const total = yarnSlots.reduce((sum, y) => sum + (parseFloat(y.total_yards) || 0), 0);
    if (total > 0) setSpecs(prev => ({ ...prev, yarnYards: total.toString() }));
  }, [yarnSlots]);

  // Load known brands for autocomplete
  useEffect(() => {
    supabase.from('yarn_library')
      .select('brand_name, brand_url, yards_per_skein, weight, fiber')
      .order('brand_name')
      .then(({ data }) => {
        if (!data) return;
        const seen = new Set();
        const brands = data.filter(d => { if (seen.has(d.brand_name)) return false; seen.add(d.brand_name); return true; });
        setKnownBrands(brands);
      });
  }, []);

  // Debounced library search
  useEffect(() => {
    if (!librarySearch.trim()) {
      setLibraryResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLibrarySearchLoading(true);
      const { data } = await supabase
        .from('yarn_library')
        .select('id, brand_name, colorway_name, brand_url, image_url, yards_per_skein, weight, fiber')
        .or(`colorway_name.ilike.%${librarySearch}%,brand_name.ilike.%${librarySearch}%`)
        .limit(10);
      const unique = (data || []).filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i);
      setLibraryResults(unique);
      setLibrarySearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [librarySearch]);

  // Multi-select toggle functions
  const toggleCategory = (category) => {
    setSpecs(prev => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category]
    }));
  };

  const toggleHook = (hook) => {
    setSpecs(prev => ({
      ...prev,
      hook: prev.hook.includes(hook)
        ? prev.hook.filter(h => h !== hook)
        : [...prev.hook, hook]
    }));
  };

  // Add/Remove functions for stitches
  const addStitch = (stitchId) => {
    const currentStitches = specs.stitches || [];
    if (stitchId && !currentStitches.includes(stitchId)) {
      setSpecs(prev => ({
        ...prev,
        stitches: [...currentStitches, stitchId]
      }));
    }
  };

  const removeStitch = (index) => {
    setSpecs(prev => ({
      ...prev,
      stitches: (prev.stitches || []).filter((_, i) => i !== index)
    }));
  };


  const colors = {
    sageGreen: "#a3b18a",
    dustyRust: "#c1795b",
    mustard: "#d4a574",
    bgCream: "#faf8f5",
    borderGreige: "#e8e0d5",
    charcoal: "#2a2a2a",
    darkGray: "#6b6b6b",
    brownGray: "#8b7355",
  };

  const fonts = {
    display: "'Pacifico', cursive",
    body: "'Karla', sans-serif",
  };

  const handleFetchPreview = async () => {
    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }

    setLoading(true);
    setError("");

    // Check if it's a YouTube URL
    const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");

    try {
      const response = await fetch(
        "https://xbuzwxzxxklktzifbdwr.supabase.co/functions/v1/extract-pattern",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhidXp3eHp4eGtsa3R6aWZiZHdyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEwOTU0OTgsImV4cCI6MjA4NjY3MTQ5OH0.VlbGxyp93QxylmPneY38i-tlfZC__D3W1dGksD4YLeM",
            "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhidXp3eHp4eGtsa3R6aWZiZHdyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEwOTU0OTgsImV4cCI6MjA4NjY3MTQ5OH0.VlbGxyp93QxylmPneY38i-tlfZC__D3W1dGksD4YLeM",
          },
          body: JSON.stringify({ url, mode: "preview" }),
        }
      );
      const data = await response.json();

      if (data.status === "success") {
        setPreview({
          title: data.data.title || "Untitled Pattern",
          description: data.data.description || "",
          image: data.data.image || "",
          siteName: isYouTube ? "YouTube" : data.data.publisher || "Unknown",
          sourceUrl: url,
        });

        // If YouTube, pre-fill video fields
        if (isYouTube) {
          setSpecs((prev) => ({
            ...prev,
            videoUrl: url,
          }));
        }

        setStep("preview");
      } else {
        setError("Could not fetch preview. Please check the URL.");
      }
    } catch (err) {
      setError("Failed to fetch preview. Try again or enter details manually.");
    } finally {
      setLoading(false);
    }
  };

  const handleExtractWithAI = async () => {
    const extractUrl = preview?.sourceUrl || url;
    if (!extractUrl) return;
    setExtracting(true);
    setError('');
    if (!preview) {
      setPreview({ title: '', description: '', image: '', sourceUrl: extractUrl, siteName: '' });
      setStep('preview');
    }
    try {
      const res = await fetch('https://xbuzwxzxxklktzifbdwr.supabase.co/functions/v1/extract-pattern', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhidXp3eHp4eGtsa3R6aWZiZHdyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEwOTU0OTgsImV4cCI6MjA4NjY3MTQ5OH0.VlbGxyp93QxylmPneY38i-tlfZC__D3W1dGksD4YLeM`,
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhidXp3eHp4eGtsa3R6aWZiZHdyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEwOTU0OTgsImV4cCI6MjA4NjY3MTQ5OH0.VlbGxyp93QxylmPneY38i-tlfZC__D3W1dGksD4YLeM',
        },
        body: JSON.stringify({ url: extractUrl }),
      });
      const responseText = await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${responseText}`);
      const extracted = JSON.parse(responseText);
      if (extracted?.error) throw new Error(extracted.error);

      // Map hook sizes: AI returns "5mm" or "6.5mm" → match full hook label
      const hookLabels = [
        "B/1 (2.25mm)", "C/2 (2.75mm)", "D/3 (3.25mm)", "E/4 (3.5mm)",
        "F/5 (3.75mm)", "G/6 (4.0mm)", "7 (4.5mm)", "H/8 (5.0mm)",
        "I/9 (5.5mm)", "J/10 (6.0mm)", "K/10.5 (6.5mm)", "L/11 (8.0mm)",
        "M/13 (9.0mm)", "N/15 (10.0mm)", "P/16 (11.5mm)", "Q (15.0mm)", "S (19.0mm)"
      ];
      const matchedHooks = (extracted.hook_sizes || []).reduce((acc, size) => {
        const mmMatch = size.match(/(\d+(?:\.\d+)?)\s*mm/i);
        if (mmMatch) {
          const targetMm = parseFloat(mmMatch[1]);
          const match = hookLabels.find(label => {
            const labelMm = label.match(/\((\d+(?:\.\d+)?)mm\)/i);
            return labelMm && Math.abs(parseFloat(labelMm[1]) - targetMm) < 0.01;
          });
          if (match && !acc.includes(match)) acc.push(match);
        }
        return acc;
      }, []);

      // Match extracted stitch names against the stitch library
      const extractedStitchNames = extracted.stitches || [];
      const matchedStitchIds = [];
      const unmatchedStitchNames = [];
      extractedStitchNames.forEach(name => {
        const lower = name.toLowerCase().trim();
        const match = stitches.find(s =>
          s.name?.toLowerCase() === lower ||
          s.abbreviation?.toLowerCase() === lower ||
          s.name?.toLowerCase().includes(lower) ||
          lower.includes(s.name?.toLowerCase())
        );
        if (match && !matchedStitchIds.includes(match.id)) {
          matchedStitchIds.push(match.id);
        } else if (!match) {
          unmatchedStitchNames.push(name);
        }
      });
      // Combine unmatched stitch names + AI's special_stitches into specialStitches field
      const specialStitchText = [
        extracted.special_stitches,
        unmatchedStitchNames.length > 0 ? unmatchedStitchNames.join(', ') : null,
      ].filter(Boolean).join('; ');

      setPreview(prev => ({
        ...prev,
        ...(extracted.title && !prev?.title && { title: extracted.title }),
        ...(extracted.image?.url && !prev?.image && { image: extracted.image.url }),
        ...(extracted.site_name && !prev?.siteName && { siteName: extracted.site_name }),
        ...(extracted.creator && !prev?.siteName && { siteName: extracted.creator }),
        sourceUrl: prev?.sourceUrl || extractUrl,
      }));
      setSpecs(prev => ({
        ...prev,
        ...(extracted.difficulty && { difficulty: extracted.difficulty }),
        ...(extracted.construction_method && { constructionMethod: extracted.construction_method }),
        ...(extracted.gauge_entries?.length > 0 && { gaugeEntries: extracted.gauge_entries }),
        ...(extracted.yarn_weight && { yarnWeight: extracted.yarn_weight }),
        ...(extracted.yarn_material && { yarnMaterial: extracted.yarn_material }),
        ...(matchedHooks.length > 0 && { hook: matchedHooks }),
        ...(extracted.total_yards != null && { yarnYards: extracted.total_yards.toString() }),
        ...(extracted.original_width != null && { originalWidth: extracted.original_width.toString() }),
        ...(extracted.original_height != null && { originalHeight: extracted.original_height.toString() }),
        ...(matchedStitchIds.length > 0 && { stitches: matchedStitchIds }),
        ...(specialStitchText && { specialStitches: specialStitchText }),
        ...(extracted.video_url && { videoUrl: extracted.video_url }),
        ...(extracted.category?.length > 0 && { category: extracted.category }),
      }));
    } catch (err) {
      setError(`AI extraction failed: ${err.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmitPattern = (submitStatus) => {

    if (
      !specs.hook ||
      specs.hook.length === 0 ||
      !specs.category ||
      specs.category.length === 0 ||
      !specs.yarnYards
    ) {
      setError(
        "Please fill in all required fields (hook size, category, yarn weight/material, gauge, and total yarn)"
      );
      return;
    }

    const patternData = {
      id: pendingEditId || editingPatternId || null,
      title: specs.customTitle || preview.title,
      slug: specs.slug || generateSlug(specs.customTitle || preview.title),
      description: preview.description,
      image: specs.customImage || preview.image,
      sourceUrl: preview.sourceUrl,
      siteName: specs.customAttribution || preview.siteName,
      difficulty: specs.difficulty,
      hook: specs.hook,
      constructionMethod: specs.constructionMethod || null,
      gaugeEntries: specs.gaugeEntries || [],
      totalYards: parseInt(specs.yarnYards),
      originalSize: {
        width: specs.originalWidth ? parseInt(specs.originalWidth) : null,
        height: specs.originalHeight ? parseInt(specs.originalHeight) : null,
      },
      yarnSlots: yarnSlots,
      publishPalette: publishPalette,
      videoUrl: specs.videoUrl || null,
      videoCreator: specs.videoCreator || null,
      curatorNotes: specs.curatorNotes || "",
      category: specs.category,
      yarnWeight: specs.yarnWeight,
      yarnMaterial: specs.yarnMaterial,
      stitches: specs.stitches || [],
      specialStitches: specs.specialStitches || "",
      video: !!specs.videoUrl,
      creator: preview.siteName || "Unknown",
      status: submitStatus || 'published',
      submitted_by: userMode ? currentUser?.id : null,
      patternContent: specs.patternContent || null,
      patternPhotos: specs.patternPhotos || [],
    };

    onAddPattern(patternData);

    if (pendingEditId) {
      const updated = pendingSubmissions.filter(s => s.id !== pendingEditId);
      setPendingSubmissions(updated);
      onPendingUpdate && onPendingUpdate(updated);
      setPendingEditId(null);
    }
    if (editingPatternId && onResetEdit) onResetEdit();

    setStep("success");
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "2rem",
        overflow: "auto",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "20px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "2.5rem",
          fontFamily: fonts.body,
        }}
      >
        {step !== "success" && (
          <div style={{ marginBottom: "2rem" }}>
            <h2
              style={{
                fontSize: "2rem",
                fontFamily: fonts.display,
                color: colors.charcoal,
                marginBottom: "0.5rem",
              }}
            >
              {userMode ? "Submit a Pattern" : editingPatternId ? "Edit Pattern" : "Add New Pattern"}
            </h2>
            <p style={{ color: colors.darkGray, fontSize: "0.95rem" }}>
              {userMode ? "Share a pattern with the community" : editingPatternId ? "Update pattern details" : "Curate a pattern for The Single Loop"}
            </p>
          </div>
        )}

        {/* Pending Review Section — admin only */}
        {!userMode && showPendingReview && pendingSubmissions.length > 0 && (
          <div style={{ marginBottom: '2rem', border: `2px solid ${colors.mustard}`, borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ background: colors.mustard, padding: '0.75rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '700', color: '#fff' }}>
                {pendingSubmissions.length} Pattern{pendingSubmissions.length > 1 ? 's' : ''} Pending Review
              </span>
              <button onClick={() => setShowPendingReview(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
            </div>
            {pendingSubmissions.map(p => (
              <div key={p.id} style={{ padding: '1rem 1.25rem', borderTop: `1px solid ${colors.borderGreige}`, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {p.image && <img src={p.image} alt={p.title} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', color: colors.charcoal, marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                  <div style={{ fontSize: '0.8rem', color: colors.darkGray }}>
                    Submitted {new Date(p.created_at).toLocaleDateString()}
                  </div>
                  {p.special_stitches && (
                    <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', color: colors.dustyRust, fontWeight: '600' }}>
                      ✦ Special Stitches: {p.special_stitches}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    onClick={() => loadPendingForEdit(p)}
                    style={{ padding: '0.4rem 0.75rem', background: colors.sageGreen, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Edit & Publish
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm(`Reject "${p.title}"? It will go back to the submitter's private library.`)) {
                        await supabase.from('patterns').update({ status: 'personal' }).eq('id', p.id);
                        const updated = pendingSubmissions.filter(s => s.id !== p.id);
                        setPendingSubmissions(updated);
                        onPendingUpdate && onPendingUpdate(updated);
                      }
                    }}
                    style={{ padding: '0.4rem 0.75rem', background: 'transparent', color: colors.dustyRust, border: `1px solid ${colors.dustyRust}`, borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "1rem",
              background: "#fff3cd",
              border: "2px solid #ffc107",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#856404",
            }}
          >
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {step === "url" && (
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "600",
                color: colors.brownGray,
                fontSize: "0.9rem",
              }}
            >
              Pattern URL *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://blog.com/crochet-pattern"
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none",
                marginBottom: "1.5rem",
              }}
              onKeyPress={(e) => e.key === "Enter" && handleFetchPreview()}
            />

            <button
              onClick={handleFetchPreview}
              disabled={loading}
              style={{
                width: "100%",
                padding: "1rem",
                background: loading
                  ? colors.borderGreige
                  : `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                color: "#fff",
                border: "none",
                borderRadius: "50px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              {loading ? (
                <>
                  <Loader
                    size={20}
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                  Fetching Preview...
                </>
              ) : (
                <>Retrieve Pattern</>
              )}
            </button>

            {error && !userMode && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  type="button"
                  onClick={handleExtractWithAI}
                  disabled={extracting}
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    background: extracting ? colors.borderGreige : `linear-gradient(135deg, ${colors.mustard} 0%, ${colors.dustyRust} 100%)`,
                    color: "#fff", border: "none", borderRadius: "50px", fontSize: "1rem", fontWeight: "600",
                    cursor: extracting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                  }}
                >
                  {extracting
                    ? <><Loader size={18} style={{ animation: "spin 1s linear infinite" }} /> Extracting pattern details...</>
                    : <>✨ Extract with AI</>}
                </button>
                <p style={{ fontSize: "0.78rem", color: colors.darkGray, textAlign: "center", marginTop: "0.4rem" }}>
                  Try AI extraction if the URL couldn't be fetched automatically
                </p>
              </div>
            )}

            <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
              <p style={{ color: colors.darkGray, fontSize: "0.85rem", marginBottom: "0.75rem" }}>— or —</p>
              <button
                onClick={() => {
                  setPreview({ title: "", description: "", image: "", sourceUrl: "", siteName: "The Single Loop" });
                  setStep("preview");
                }}
                style={{ padding: "0.75rem 1.5rem", background: colors.dustyRust, color: "#fff", border: "none", borderRadius: "50px", fontSize: "0.95rem", fontWeight: "600", cursor: "pointer" }}
              >
                Create Pattern
              </button>
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div>
            <div
              style={{
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "12px",
                overflow: "hidden",
                marginBottom: "2rem",
              }}
            >
              {preview.image && (
                <img
                  src={specs.customImage || preview.image}
                  alt={preview.title}
                  style={{
                    width: "100%",
                    height: "200px",
                    objectFit: "cover",
                  }}
                />
              )}
              <div style={{ padding: "1rem" }}>
                <h3
                  style={{
                    fontSize: "1.2rem",
                    color: colors.charcoal,
                    marginBottom: "0.5rem",
                    fontWeight: "700",
                  }}
                >
                  {preview.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: colors.darkGray,
                    marginBottom: "0.5rem",
                  }}
                >
                  {preview.description}
                </p>
                <p style={{ fontSize: "0.85rem", color: colors.brownGray }}>
                  From: {preview.siteName}
                </p>
              </div>
            </div>

            {/* Custom Image Override */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Custom Image URL (optional)
              </label>
              <input
                type="url"
                value={specs.customImage}
                onChange={(e) =>
                  setSpecs({ ...specs, customImage: e.target.value })
                }
                placeholder="Leave blank to use scraped image"
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                }}
              />
              <p
                style={{
                  fontSize: "0.75rem",
                  color: colors.darkGray,
                  marginTop: "0.5rem",
                  fontStyle: "italic",
                }}
              >
                Paste a different image URL to override the scraped image
              </p>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                Pattern Title (optional override)
              </label>
              <input
                type="text"
                value={specs.customTitle}
                onChange={(e) => {
                  const title = e.target.value;
                  setSpecs(s => ({ ...s, customTitle: title, slug: generateSlug(title) }));
                }}
                placeholder={preview.title}
                style={{ width: "100%", padding: "0.875rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "1rem", outline: "none" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                URL Slug
              </label>
              <input
                type="text"
                value={specs.slug || generateSlug(specs.customTitle || preview.title)}
                onChange={(e) => setSpecs(s => ({ ...s, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                placeholder="auto-generated from title"
                style={{ width: "100%", padding: "0.875rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "1rem", outline: "none", fontFamily: "monospace" }}
              />
              <p style={{ fontSize: "0.75rem", color: colors.darkGray, marginTop: "0.5rem", fontStyle: "italic" }}>
                Will appear as: /patterns/{specs.slug || generateSlug(specs.customTitle || preview.title)}
              </p>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Custom Attribution (optional)
              </label>
              <input
                type="text"
                value={specs.customAttribution}
                onChange={(e) =>
                  setSpecs({ ...specs, customAttribution: e.target.value })
                }
                placeholder={`Leave blank to use: ${preview.siteName}`}
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                }}
              />
              <p
                style={{
                  fontSize: "0.75rem",
                  color: colors.darkGray,
                  marginTop: "0.5rem",
                  fontStyle: "italic",
                }}
              >
                Use this to override the "From" field. Example: "Pattern from
                Bella Coco, video by Marly Bird"
              </p>
            </div>
            {/* Extract with AI button */}
            {!userMode && (
              <div style={{ marginBottom: "1.5rem" }}>
                <button
                  type="button"
                  onClick={handleExtractWithAI}
                  disabled={extracting}
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    background: extracting ? colors.borderGreige : `linear-gradient(135deg, ${colors.mustard} 0%, ${colors.dustyRust} 100%)`,
                    color: "#fff",
                    border: "none",
                    borderRadius: "50px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: extracting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  {extracting ? (
                    <><Loader size={18} style={{ animation: "spin 1s linear infinite" }} /> Extracting pattern details...</>
                  ) : (
                    <>✨ Extract with AI</>
                  )}
                </button>
                <p style={{ fontSize: "0.78rem", color: colors.darkGray, textAlign: "center", marginTop: "0.4rem" }}>
                  Auto-fills difficulty, gauge, hook sizes, yarn weight, dimensions, and more
                </p>
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.25rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Difficulty *
                </label>
                <select
                  value={specs.difficulty}
                  onChange={(e) =>
                    setSpecs({ ...specs, difficulty: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                  }}
                >
                  <option>Beginner Friendly</option>
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Categories * (Select all that apply)
                </label>
                <div style={{ 
                  display: "flex", 
                  flexWrap: "wrap", 
                  gap: "0.5rem",
                  padding: "0.875rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  background: colors.bgCream,
                }}>
                  {["Clothing", "Accessories", "Home Decor", "Blankets", "Kids", "Holiday", "Pet"].map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      style={{
                        padding: "0.5rem 1rem",
                        background: specs.category.includes(cat) ? colors.sageGreen : "#fff",
                        color: specs.category.includes(cat) ? "#fff" : colors.brownGray,
                        border: `2px solid ${specs.category.includes(cat) ? colors.sageGreen : colors.borderGreige}`,
                        borderRadius: "50px",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>


              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Hook Sizes * (Select all that apply)
                </label>
                <div style={{ 
                  display: "flex", 
                  flexWrap: "wrap", 
                  gap: "0.5rem",
                  padding: "0.875rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  background: colors.bgCream,
                  maxHeight: "200px",
                  overflowY: "auto",
                }}>
                  {[
                    "B/1 (2.25mm)", "C/2 (2.75mm)", "D/3 (3.25mm)", "E/4 (3.5mm)", 
                    "F/5 (3.75mm)", "G/6 (4.0mm)", "7 (4.5mm)", "H/8 (5.0mm)", 
                    "I/9 (5.5mm)", "J/10 (6.0mm)", "K/10.5 (6.5mm)", "L/11 (8.0mm)", 
                    "M/13 (9.0mm)", "N/15 (10.0mm)", "P/16 (11.5mm)", "Q (15.0mm)", "S (19.0mm)"
                  ].map(hookSize => (
                    <button
                      key={hookSize}
                      type="button"
                      onClick={() => toggleHook(hookSize)}
                      style={{
                        padding: "0.5rem 0.75rem",
                        background: specs.hook.includes(hookSize) ? colors.sageGreen : "#fff",
                        color: specs.hook.includes(hookSize) ? "#fff" : colors.brownGray,
                        border: `2px solid ${specs.hook.includes(hookSize) ? colors.sageGreen : colors.borderGreige}`,
                        borderRadius: "50px",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {hookSize}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stitches Used */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray }}>
                  Stitches Used
                </label>
                
                {/* Current selected stitches */}
                <div style={{ marginBottom: "1rem" }}>
                  {(specs.stitches || []).map((stitchId, idx) => {
                    const stitch = stitches.find(s => s.id === stitchId);
                    return stitch ? (
                      <div key={idx} style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "0.5rem", 
                        padding: "0.5rem", 
                        background: colors.bgCream, 
                        borderRadius: "8px",
                        marginBottom: "0.5rem"
                      }}>
                        <span style={{ flex: 1 }}>
                          <strong>{stitch.abbreviation}</strong> - {stitch.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeStitch(idx)}
                          style={{
                            background: colors.dustyRust,
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            fontWeight: "bold"
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              
                {/* Add stitch dropdown */}
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) addStitch(e.target.value);
                      e.target.value = ""; // Reset
                    }}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "0.95rem"
                    }}
                  >
                    <option value="">Select a stitch...</option>
                    {stitches
                      .filter(s => !(specs.stitches || []).includes(s.id)) // Don't show already selected
                      .map(stitch => (
                        <option key={stitch.id} value={stitch.id}>
                          {stitch.abbreviation} - {stitch.name}
                        </option>
                      ))}
                  </select>
                  
                  {/* Add New Stitch button */}
                  <button
                    type="button"
                    onClick={() => setShowStitchAdmin(true)}
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: colors.sageGreen,
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      whiteSpace: "nowrap"
                    }}
                  >
                    + New Stitch
                  </button>
                </div>
              </div>

              {/* Special Stitches */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray }}>
                  Special Stitches <span style={{ fontWeight: 400, color: colors.darkGray }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={specs.specialStitches || ""}
                  onChange={(e) => setSpecs({ ...specs, specialStitches: e.target.value })}
                  placeholder="e.g. Crocodile Stitch, Bavarian Crochet"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                    boxSizing: "border-box"
                  }}
                />
                <p style={{ fontSize: "0.82rem", color: colors.darkGray, marginTop: "0.35rem" }}>
                  For stitches not in the library. Separate multiple stitches with commas.
                </p>
              </div>

              {/* Construction Method */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                  Construction Method
                </label>
                <select
                  value={specs.constructionMethod}
                  onChange={(e) => setSpecs({ ...specs, constructionMethod: e.target.value })}
                  style={{ width: "100%", padding: "0.875rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "1rem", outline: "none", background: "#fff", color: specs.constructionMethod ? colors.charcoal : colors.brownGray }}
                >
                  <option value="">Select construction method...</option>
                  <option value="Rows">Rows</option>
                  <option value="In the Round">In the Round</option>
                  <option value="Corner to Corner (C2C)">Corner to Corner (C2C)</option>
                  <option value="Granny Square">Granny Square</option>
                  <option value="Tapestry / Mosaic">Tapestry / Mosaic</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Gauge Entries */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                  Gauge
                </label>

                {/* Existing gauge entries */}
                {(specs.gaugeEntries || []).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
                    {(specs.gaugeEntries || []).map((entry, idx) => {
                      let label = '';
                      const unitLabel = entry.unit === '2in' ? '2"' : '4"';
                      if (entry.type === 'standard') label = `${entry.stitches} st${entry.rows ? ` × ${entry.rows} rows` : ''} = ${unitLabel}${entry.label ? ` (${entry.label})` : ''}`;
                      else if (entry.type === 'granny') label = `Finished square = ${entry.square_inches}"${entry.label ? ` (${entry.label})` : ''}`;
                      else if (entry.type === 'c2c') label = `${entry.blocks} blocks = ${unitLabel}${entry.label ? ` (${entry.label})` : ''}`;
                      else if (entry.type === 'note') label = entry.note || '';
                      return (
                        <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 0.75rem", background: colors.bgCream, border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", color: colors.charcoal }}>
                          <span>{label}</span>
                          <button type="button" onClick={() => setSpecs(prev => ({ ...prev, gaugeEntries: prev.gaugeEntries.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", cursor: "pointer", color: colors.darkGray, fontSize: "1rem", padding: "0 0.25rem" }}>×</button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add gauge entry form */}
                {showGaugeForm ? (
                  <div style={{ padding: "0.875rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", background: "#fff" }}>
                    <div style={{ marginBottom: "0.75rem" }}>
                      <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", fontWeight: "600", color: colors.brownGray }}>Gauge Type</label>
                      <select value={gaugeForm.type} onChange={(e) => setGaugeForm({ ...gaugeForm, type: e.target.value })} style={{ width: "100%", padding: "0.625rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem" }}>
                        <option value="standard">Rows / In the Round (stitches × rows)</option>
                        <option value="granny">Granny Square (finished size)</option>
                        <option value="c2c">Corner to Corner / C2C (blocks)</option>
                        <option value="note">Note / Other (free text)</option>
                      </select>
                    </div>

                    {gaugeForm.type === 'standard' && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem", marginBottom: "0.75rem" }}>
                        <div>
                          <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Stitches</label>
                          <input type="number" value={gaugeForm.stitches} onChange={(e) => setGaugeForm({ ...gaugeForm, stitches: e.target.value })} placeholder="16" style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Rows</label>
                          <input type="number" value={gaugeForm.rows} onChange={(e) => setGaugeForm({ ...gaugeForm, rows: e.target.value })} placeholder="18" style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Per</label>
                          <select value={gaugeForm.unit} onChange={(e) => setGaugeForm({ ...gaugeForm, unit: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem" }}>
                            <option value="2in">2 inches</option>
                            <option value="4in">4 inches</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {gaugeForm.type === 'granny' && (
                      <div style={{ marginBottom: "0.75rem" }}>
                        <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Finished Square Size (inches)</label>
                        <input type="number" step="0.5" value={gaugeForm.square_inches} onChange={(e) => setGaugeForm({ ...gaugeForm, square_inches: e.target.value })} placeholder="4" style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box" }} />
                      </div>
                    )}

                    {gaugeForm.type === 'c2c' && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "0.75rem" }}>
                        <div>
                          <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Blocks</label>
                          <input type="number" value={gaugeForm.blocks} onChange={(e) => setGaugeForm({ ...gaugeForm, blocks: e.target.value })} placeholder="8" style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Per</label>
                          <select value={gaugeForm.unit} onChange={(e) => setGaugeForm({ ...gaugeForm, unit: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem" }}>
                            <option value="2in">2 inches</option>
                            <option value="4in">4 inches</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {gaugeForm.type === 'note' && (
                      <div style={{ marginBottom: "0.75rem" }}>
                        <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Note</label>
                        <input type="text" value={gaugeForm.note} onChange={(e) => setGaugeForm({ ...gaugeForm, note: e.target.value })} placeholder="e.g. Mosaic gauge: 18 sc × 36 rows = 4 inches" style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box" }} />
                      </div>
                    )}

                    <div style={{ marginBottom: "0.75rem" }}>
                      <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.82rem", color: colors.brownGray }}>Label (optional)</label>
                      <input type="text" value={gaugeForm.label} onChange={(e) => setGaugeForm({ ...gaugeForm, label: e.target.value })} placeholder="e.g. Main, Border" style={{ width: "100%", padding: "0.5rem", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box" }} />
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button type="button" onClick={() => {
                        const entry = { type: gaugeForm.type };
                        if (gaugeForm.type === 'standard') {
                          if (gaugeForm.stitches) entry.stitches = parseFloat(gaugeForm.stitches);
                          if (gaugeForm.rows) entry.rows = parseFloat(gaugeForm.rows);
                          entry.unit = gaugeForm.unit;
                        } else if (gaugeForm.type === 'granny') {
                          if (gaugeForm.square_inches) entry.square_inches = parseFloat(gaugeForm.square_inches);
                        } else if (gaugeForm.type === 'c2c') {
                          if (gaugeForm.blocks) entry.blocks = parseFloat(gaugeForm.blocks);
                          entry.unit = gaugeForm.unit;
                        } else if (gaugeForm.type === 'note') {
                          entry.note = gaugeForm.note;
                        }
                        if (gaugeForm.label) entry.label = gaugeForm.label;
                        setSpecs(prev => ({ ...prev, gaugeEntries: [...(prev.gaugeEntries || []), entry] }));
                        setGaugeForm({ type: 'standard', stitches: '', rows: '', unit: '4in', square_inches: '', blocks: '', note: '', label: '' });
                        setShowGaugeForm(false);
                      }} style={{ padding: "0.5rem 1rem", background: colors.sageGreen, color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem", fontWeight: "600" }}>
                        Add
                      </button>
                      <button type="button" onClick={() => { setShowGaugeForm(false); setGaugeForm({ type: 'standard', stitches: '', rows: '', unit: '4in', square_inches: '', blocks: '', note: '', label: '' }); }} style={{ padding: "0.5rem 1rem", background: "none", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem", color: colors.darkGray }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setShowGaugeForm(true)} style={{ padding: "0.5rem 0.875rem", background: "none", border: `1px dashed ${colors.borderGreige}`, borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", color: colors.brownGray, width: "100%" }}>
                    + Add gauge entry
                  </button>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1rem",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      color: colors.brownGray,
                      fontSize: "0.9rem",
                    }}
                  >
                    Original Width (inches)
                  </label>
                  <input
                    type="number"
                    value={specs.originalWidth}
                    onChange={(e) =>
                      setSpecs({ ...specs, originalWidth: e.target.value })
                    }
                    placeholder="45"
                    style={{
                      width: "100%",
                      padding: "0.875rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "1rem",
                      outline: "none",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      color: colors.brownGray,
                      fontSize: "0.9rem",
                    }}
                  >
                    Original Height (inches)
                  </label>
                  <input
                    type="number"
                    value={specs.originalHeight}
                    onChange={(e) =>
                      setSpecs({ ...specs, originalHeight: e.target.value })
                    }
                    placeholder="60"
                    style={{
                      width: "100%",
                      padding: "0.875rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "1rem",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Total Yarn Needed * (auto-fills from yarn colors below, or enter manually)
                </label>
                <input
                  type="number"
                  value={specs.yarnYards}
                  onChange={(e) => { setYarnYardsManuallySet(true); setSpecs({ ...specs, yarnYards: e.target.value }); }}
                  placeholder="e.g. 400"
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                  }}
                />
              </div>

              {/* Yarn Colors — library search + slots */}
              <div
                style={{
                  background: colors.bgCream,
                  padding: "1.25rem",
                  borderRadius: "12px",
                  border: `2px solid ${colors.borderGreige}`,
                }}
              >
                <label style={{ fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem", display: "block", marginBottom: "1rem" }}>
                  Yarn Colors
                </label>

                {/* Library search */}
                <div style={{ position: "relative", marginBottom: "1rem" }}>
                  <input
                    type="text"
                    value={librarySearch}
                    onChange={(e) => setLibrarySearch(e.target.value)}
                    placeholder="Search yarn library (brand or colorway)..."
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      paddingRight: librarySearch ? "2.5rem" : "0.75rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {librarySearch && (
                    <button type="button" onClick={() => { setLibrarySearch(''); setLibraryResults([]); }}
                      style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: colors.brownGray, fontSize: "1.1rem", lineHeight: 1, padding: 0 }}>×</button>
                  )}
                  {librarySearchLoading && (
                    <Loader size={16} style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", color: colors.darkGray, animation: "spin 1s linear infinite" }} />
                  )}
                  {libraryResults.length > 0 && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      zIndex: 50,
                      maxHeight: "240px",
                      overflowY: "auto",
                    }}>
                      {libraryResults.map(result => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => {
                            setYarnSlots(prev => [...prev, { ...result, yards_per_skein: result.yards_per_skein?.toString() || '', total_yards: '' }]);
                            setLibrarySearch('');
                            setLibraryResults([]);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            width: "100%",
                            padding: "0.75rem 1rem",
                            background: "transparent",
                            border: "none",
                            borderBottom: `1px solid ${colors.borderGreige}`,
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <div style={{ width: "36px", height: "36px", borderRadius: "6px", overflow: "hidden", flexShrink: 0, background: colors.borderGreige }}>
                            {result.image_url
                              ? <img src={result.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <div style={{ width: "100%", height: "100%", background: colors.borderGreige }} />
                            }
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: "600", color: colors.charcoal, fontSize: "0.875rem" }}>{result.brand_name} — {result.colorway_name}</div>
                            <div style={{ fontSize: "0.8rem", color: colors.darkGray }}>{[result.weight, result.fiber, result.yards_per_skein ? `${result.yards_per_skein} yds/skein` : null].filter(Boolean).join(' · ')}</div>
                          </div>
                          <span style={{ fontSize: "0.8rem", color: colors.sageGreen, fontWeight: "600" }}>+ Add</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Current slots */}
                {yarnSlots.length > 0 && (
                  <div style={{ marginBottom: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {yarnSlots.map((slot, idx) => (
                      <div key={idx} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        background: "#fff",
                        padding: "0.75rem",
                        borderRadius: "8px",
                        border: `2px solid ${colors.borderGreige}`,
                      }}>
                        <div style={{ width: "48px", height: "48px", borderRadius: "8px", overflow: "hidden", flexShrink: 0, background: colors.borderGreige }}>
                          {slot.image_url
                            ? <img src={slot.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <div style={{ width: "100%", height: "100%", background: colors.borderGreige }} />
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: "600", color: colors.charcoal, fontSize: "0.875rem" }}>{slot.brand_name} — {slot.colorway_name}</div>
                          <div style={{ fontSize: "0.8rem", color: colors.darkGray }}>{[slot.weight, slot.fiber, slot.role].filter(Boolean).join(' · ')}</div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.2rem" }}>
                          <input
                            type="number"
                            value={slot.total_yards || ''}
                            onChange={(e) => setYarnSlots(prev => prev.map((s, i) => i === idx ? { ...s, total_yards: e.target.value } : s))}
                            placeholder="total yds"
                            title="Total yards for this color"
                            style={{ width: "84px", padding: "0.4rem 0.5rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.75rem", outline: "none", textAlign: "center" }}
                          />
                          {slot.total_yards && slot.yards_per_skein && (
                            <span style={{ fontSize: "0.7rem", color: colors.brownGray }}>
                              ≈ {Math.ceil(parseFloat(slot.total_yards) / parseFloat(slot.yards_per_skein))} skeins
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setYarnSlots(prev => prev.filter((_, i) => i !== idx))}
                          style={{ background: "transparent", border: "none", color: colors.dustyRust, cursor: "pointer", fontSize: "1.3rem", lineHeight: 1, flexShrink: 0 }}
                          title="Remove"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new yarn form */}
                {!showAddForm ? (
                  <button
                    type="button"
                    onClick={() => setShowAddForm(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      padding: "0.5rem 1rem",
                      background: "transparent",
                      border: `2px dashed ${colors.borderGreige}`,
                      borderRadius: "8px",
                      color: colors.brownGray,
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <Plus size={14} /> Add new yarn
                  </button>
                ) : (
                  <div style={{ background: "#fff", padding: "1rem", borderRadius: "8px", border: `2px solid ${colors.borderGreige}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "0.5rem" }}>
                      {/* Brand name with autocomplete */}
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          placeholder="Brand name *"
                          value={newYarn.brand_name}
                          onChange={(e) => {
                            setNewYarn(p => ({ ...p, brand_name: e.target.value }));
                            setBrandDropdownOpen(true);
                          }}
                          onFocus={() => setBrandDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setBrandDropdownOpen(false), 150)}
                          style={{ width: "100%", padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none", boxSizing: "border-box" }}
                        />
                        {brandDropdownOpen && newYarn.brand_name && (
                          (() => {
                            const matches = knownBrands.filter(b => b.brand_name.toLowerCase().includes(newYarn.brand_name.toLowerCase()));
                            return matches.length > 0 ? (
                              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, maxHeight: "180px", overflowY: "auto" }}>
                                {matches.map((b, i) => (
                                  <div key={i}
                                    onMouseDown={() => {
                                      setNewYarn(p => ({ ...p, brand_name: b.brand_name, brand_url: b.brand_url || p.brand_url, yards_per_skein: b.yards_per_skein?.toString() || p.yards_per_skein, weight: b.weight || p.weight, fiber: b.fiber || p.fiber }));
                                      setBrandDropdownOpen(false);
                                    }}
                                    style={{ padding: "0.5rem 0.75rem", cursor: "pointer", fontSize: "0.875rem", borderBottom: `1px solid ${colors.borderGreige}` }}
                                    onMouseEnter={e => e.currentTarget.style.background = colors.bgCream}
                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                  >
                                    <div style={{ fontWeight: "600", color: colors.charcoal }}>{b.brand_name}</div>
                                    <div style={{ fontSize: "0.75rem", color: colors.brownGray }}>{[b.weight, b.fiber, b.yards_per_skein ? `${b.yards_per_skein} yds/skein` : null].filter(Boolean).join(' · ')}</div>
                                  </div>
                                ))}
                              </div>
                            ) : null;
                          })()
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Colorway name *"
                        value={newYarn.colorway_name}
                        onChange={(e) => setNewYarn(p => ({ ...p, colorway_name: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none" }}
                      />
                      <select
                        value={newYarn.weight}
                        onChange={(e) => setNewYarn(p => ({ ...p, weight: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none", background: "#fff", color: newYarn.weight ? colors.charcoal : colors.brownGray }}
                      >
                        <option value="">Select weight</option>
                        {['0 Lace','1 Super Fine','2 Fine','3 Light','4 Medium/Worsted','5 Bulky','6 Super Bulky','7 Jumbo'].map(w => <option key={w} value={w}>{w}</option>)}
                      </select>
                      <select
                        value={newYarn.fiber}
                        onChange={(e) => setNewYarn(p => ({ ...p, fiber: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none", background: "#fff", color: newYarn.fiber ? colors.charcoal : colors.brownGray }}
                      >
                        <option value="">Select fiber</option>
                        {['Acrylic','Bamboo','Cotton','Wool','Chenille','Nylon','Recycled','Blend'].map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <input
                        type="number"
                        placeholder="Yards per skein"
                        value={newYarn.yards_per_skein}
                        onChange={(e) => setNewYarn(p => ({ ...p, yards_per_skein: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none" }}
                      />
                      <div>
                        <input
                          type="number"
                          placeholder="Total yards for this color"
                          value={newYarn.total_yards}
                          onChange={(e) => setNewYarn(p => ({ ...p, total_yards: e.target.value }))}
                          style={{ width: "100%", padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none", boxSizing: "border-box" }}
                        />
                        {newYarn.total_yards && newYarn.yards_per_skein && (
                          <div style={{ fontSize: "0.75rem", color: colors.brownGray, marginTop: "0.2rem", paddingLeft: "0.25rem" }}>
                            ≈ {Math.ceil(parseFloat(newYarn.total_yards) / parseFloat(newYarn.yards_per_skein))} skeins
                          </div>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Role (e.g. Main Color)"
                        value={newYarn.role}
                        onChange={(e) => setNewYarn(p => ({ ...p, role: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none" }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
                      <input
                        type="url"
                        placeholder="Product URL (optional)"
                        value={newYarn.brand_url}
                        onChange={(e) => setNewYarn(p => ({ ...p, brand_url: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none" }}
                      />
                      <input
                        type="url"
                        placeholder="Image URL (optional)"
                        value={newYarn.image_url}
                        onChange={(e) => setNewYarn(p => ({ ...p, image_url: e.target.value }))}
                        style={{ padding: "0.625rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "6px", fontSize: "0.875rem", outline: "none" }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (!newYarn.brand_name.trim() || !newYarn.colorway_name.trim()) return;
                          setYarnSlots(prev => [...prev, { ...newYarn }]);
                          setNewYarn({ brand_name: '', colorway_name: '', brand_url: '', image_url: '', yards_per_skein: '', weight: '', fiber: '', total_yards: '', role: '' });
                          setShowAddForm(false);
                        }}
                        style={{ flex: 1, padding: "0.625rem", background: colors.sageGreen, color: "#fff", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "0.875rem" }}
                      >
                        Add Yarn
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        style={{ padding: "0.625rem 1rem", background: "transparent", color: colors.darkGray, border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", cursor: "pointer", fontSize: "0.875rem" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Publish to palette library */}
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem", cursor: "pointer", fontSize: "0.875rem", color: colors.brownGray }}>
                  <input
                    type="checkbox"
                    checked={publishPalette}
                    onChange={(e) => setPublishPalette(e.target.checked)}
                    style={{ accentColor: colors.sageGreen, width: "16px", height: "16px" }}
                  />
                  <span>Publish as palette in Color Palettes library</span>
                </label>
              </div>

              {/* Video Tutorial Section */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Video Tutorial URL (optional)
                </label>
                <input
                  type="url"
                  value={specs.videoUrl}
                  onChange={(e) =>
                    setSpecs({ ...specs, videoUrl: e.target.value })
                  }
                  placeholder="https://youtube.com/watch?v=..."
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                  }}
                />
              </div>

              {specs.videoUrl && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      color: colors.brownGray,
                      fontSize: "0.9rem",
                    }}
                  >
                    Video Creator Name (optional)
                  </label>
                  <input
                    type="text"
                    value={specs.videoCreator}
                    onChange={(e) =>
                      setSpecs({ ...specs, videoCreator: e.target.value })
                    }
                    placeholder="e.g., Bella Coco"
                    style={{
                      width: "100%",
                      padding: "0.875rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "1rem",
                      outline: "none",
                    }}
                  />
                </div>
              )}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Your Curator Notes (optional)
                </label>
                <textarea
                  value={specs.curatorNotes}
                  onChange={(e) =>
                    setSpecs({ ...specs, curatorNotes: e.target.value })
                  }
                  placeholder="Why you love this pattern, any tips..."
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                    fontFamily: fonts.body,
                    resize: "vertical",
                  }}
                />
              </div>
            </div>

            {/* Pattern Content — for original patterns */}
            <div style={{ marginTop: "2rem", paddingTop: "2rem", borderTop: `2px solid ${colors.borderGreige}` }}>
              <h3 style={{ fontFamily: fonts.display, color: colors.charcoal, marginBottom: "0.25rem", fontSize: "1.2rem" }}>
                Pattern Content
              </h3>
              <p style={{ fontSize: "0.85rem", color: colors.darkGray, marginBottom: "1.5rem" }}>
                For original patterns only. Leave blank if linking to an external source.
              </p>

              {/* Photos */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                  Pattern Photos (URLs)
                </label>
                {(specs.patternPhotos || []).map((url, idx) => (
                  <div key={idx} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => {
                        const updated = [...specs.patternPhotos];
                        updated[idx] = e.target.value;
                        setSpecs(s => ({ ...s, patternPhotos: updated }));
                      }}
                      placeholder="https://..."
                      style={{ flex: 1, padding: "0.75rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "0.95rem", outline: "none" }}
                    />
                    <button type="button" onClick={() => setSpecs(s => ({ ...s, patternPhotos: s.patternPhotos.filter((_, i) => i !== idx) }))}
                      style={{ padding: "0.75rem", background: colors.dustyRust, color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", width: "40px" }}>×</button>
                  </div>
                ))}
                <button type="button" onClick={() => setSpecs(s => ({ ...s, patternPhotos: [...(s.patternPhotos || []), ""] }))}
                  style={{ padding: "0.5rem 1rem", background: colors.sageGreen, color: "#fff", border: "none", borderRadius: "50px", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer" }}>
                  + Add Photo
                </button>
              </div>

              {/* Sections */}
              {(specs.patternContent?.sections || []).map((section, sIdx) => (
                <div key={sIdx} style={{ marginBottom: "2rem", background: colors.bgCream, borderRadius: "12px", padding: "1.25rem" }}>
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", alignItems: "center" }}>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => {
                        const sections = [...specs.patternContent.sections];
                        sections[sIdx] = { ...sections[sIdx], title: e.target.value };
                        setSpecs(s => ({ ...s, patternContent: { ...s.patternContent, sections } }));
                      }}
                      placeholder="Section title (e.g. Materials, Instructions)"
                      style={{ flex: 1, padding: "0.75rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "1rem", fontWeight: "600", outline: "none" }}
                    />
                    {specs.patternContent.sections.length > 1 && (
                      <button type="button" onClick={() => {
                        const sections = specs.patternContent.sections.filter((_, i) => i !== sIdx);
                        setSpecs(s => ({ ...s, patternContent: { ...s.patternContent, sections } }));
                      }} style={{ padding: "0.75rem", background: colors.dustyRust, color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", width: "40px" }}>×</button>
                    )}
                  </div>
                  {section.steps.map((step, stIdx) => (
                    <div key={stIdx} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "flex-start" }}>
                      <span style={{ paddingTop: "0.75rem", fontWeight: "700", color: colors.brownGray, minWidth: "1.5rem", fontSize: "0.9rem" }}>{stIdx + 1}.</span>
                      <textarea
                        value={step}
                        onChange={(e) => {
                          const sections = [...specs.patternContent.sections];
                          const steps = [...sections[sIdx].steps];
                          steps[stIdx] = e.target.value;
                          sections[sIdx] = { ...sections[sIdx], steps };
                          setSpecs(s => ({ ...s, patternContent: { ...s.patternContent, sections } }));
                        }}
                        placeholder={`Step ${stIdx + 1}`}
                        rows={2}
                        style={{ flex: 1, padding: "0.75rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "0.95rem", outline: "none", fontFamily: fonts.body, resize: "vertical" }}
                      />
                      {section.steps.length > 1 && (
                        <button type="button" onClick={() => {
                          const sections = [...specs.patternContent.sections];
                          sections[sIdx] = { ...sections[sIdx], steps: sections[sIdx].steps.filter((_, i) => i !== stIdx) };
                          setSpecs(s => ({ ...s, patternContent: { ...s.patternContent, sections } }));
                        }} style={{ padding: "0.75rem", background: colors.dustyRust, color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", width: "40px", marginTop: "0" }}>×</button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    const sections = [...specs.patternContent.sections];
                    sections[sIdx] = { ...sections[sIdx], steps: [...sections[sIdx].steps, ""] };
                    setSpecs(s => ({ ...s, patternContent: { ...s.patternContent, sections } }));
                  }} style={{ padding: "0.4rem 0.9rem", background: colors.sageGreen, color: "#fff", border: "none", borderRadius: "50px", fontSize: "0.8rem", fontWeight: "600", cursor: "pointer", marginTop: "0.5rem" }}>
                    + Add Step
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => {
                const sections = [...(specs.patternContent?.sections || []), { title: "", steps: [""] }];
                setSpecs(s => ({ ...s, patternContent: { ...s.patternContent, sections } }));
              }} style={{ padding: "0.5rem 1rem", background: colors.brownGray, color: "#fff", border: "none", borderRadius: "50px", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer" }}>
                + Add Section
              </button>
            </div>

            <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
              <button
                onClick={() => setStep("url")}
                style={{
                  flex: 1,
                  padding: "1rem",
                  background: "#fff",
                  color: colors.brownGray,
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "50px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                ← Back
              </button>
              {userMode ? (
                <div style={{ display: 'flex', gap: '0.75rem', flex: 2 }}>
                  <button
                    onClick={() => handleSubmitPattern('personal')}
                    style={{
                      flex: 1,
                      padding: "1rem",
                      background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                      color: "#fff",
                      border: "none",
                      borderRadius: "50px",
                      fontSize: "0.95rem",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Save to My Library
                  </button>
                  {!showShareDisclaimer ? (
                    <button
                      onClick={() => setShowShareDisclaimer(true)}
                      style={{
                        flex: 1,
                        padding: "1rem",
                        background: `linear-gradient(135deg, ${colors.mustard} 0%, #c8a84b 100%)`,
                        color: "#fff",
                        border: "none",
                        borderRadius: "50px",
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Submit for Sharing
                    </button>
                  ) : (
                    <div style={{ flex: 1, background: '#fffbf0', border: `1px solid ${colors.borderGreige}`, borderRadius: '12px', padding: '0.75rem', fontSize: '0.8rem', color: colors.darkGray }}>
                      <p style={{ margin: '0 0 0.5rem' }}>Your pattern will be reviewed before sharing. Our team may make minor edits. Submissions that don't meet guidelines may not be published.</p>
                      <button
                        onClick={() => handleSubmitPattern('pending')}
                        style={{ width: '100%', padding: '0.5rem', background: colors.mustard, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        I understand — Submit
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleSubmitPattern('published')}
                  style={{
                    flex: 2,
                    padding: "1rem",
                    background: `linear-gradient(135deg, ${colors.dustyRust} 0%, #a8674f 100%)`,
                    color: "#fff",
                    border: "none",
                    borderRadius: "50px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Plus size={20} />
                  {pendingEditId ? 'Publish Pattern' : editingPatternId ? 'Update Pattern' : 'Add Pattern'}
                </button>
              )}
            </div>
          </div>
        )}

        {step === "success" && (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.5rem",
              }}
            >
              <Check size={40} color="#fff" />
            </div>
            <h3
              style={{
                fontSize: "1.5rem",
                color: colors.charcoal,
                marginBottom: "0.5rem",
                fontFamily: fonts.display,
              }}
            >
              Pattern Added!
            </h3>
            <p style={{ color: colors.darkGray }}>
              {userMode
                ? "Visit your Projects Dashboard to view your personal saved pattern"
                : "Successfully curated to The Single Loop"}
            </p>
          </div>
        )}

      {step !== "success" && (
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            background: "transparent",
            border: "none",
            fontSize: "1.5rem",
            color: colors.brownGray,
            cursor: "pointer",
            padding: "0.5rem",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
      
      {/* Stitch Admin Modal */}
      {showStitchAdmin && (
        <StitchAdmin
          onClose={() => setShowStitchAdmin(false)}
          onSave={async () => {
            if (onStitchSaved) await onStitchSaved();
            setShowStitchAdmin(false);
          }}
          editingStitch={null}
        />
      )}
    </div> 
    
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div> 
);
}
// ========================================
// STITCH ADMIN COMPONENT
// ========================================

export default CuratorAdmin;
